import { EmailOperator, PlusIcon, TrashIcon } from "../../shared/Icons";
import { themeAtom } from "../../shared/atoms";
import type { VesselContactDetailsProps } from "../models/tech-form-page.models";
import { Tq1VesselDetailsM } from "../models/tq1-vessel-details.models";
import {
  CountryCodes,
  GlobalStarSatelliteCodes,
  InmarsatSatelliteCodes,
  InmarsatTelexCodes,
  IridiumSatelliteCodes,
  TelexCodes,
  ThurayaSatelliteCodes,
} from "./PrefixCodes";
import { ListBulletIcon, QuestionMarkCircleIcon } from "@heroicons/react/20/solid";
import { DevicePhoneMobileIcon, PhoneIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Paths } from "type-fest";

function VesselContactDetails<T extends Tq1VesselDetailsM>({
  form,
  contactDetailsErrors,
  handleInputChange,
  addContactDetail,
  removeContactDetail,
}: VesselContactDetailsProps<T>) {
  const theme = useAtomValue(themeAtom);

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleBlur = () => {
    setTimeout(() => setFocusedIndex(null), 100);
  };

  return (
    <div className="my-2 flex flex-col">
      <label className="mb-6 block text-xl font-medium">Contact Details</label>
      <div className="mb-2 flex w-max flex-row items-center">
        <label className="ml-[170px] text-sm font-semibold">Provider</label>
        <label className="ml-[110px] text-sm font-semibold">Contact</label>
        <label className="ml-[450px] text-sm font-semibold">Where is located?</label>
        <label className="ml-[65px] text-sm font-semibold">Preferred contact</label>
      </div>
      {form.contactDetails.map((contact, index) => {
        const communicationTypeError = contactDetailsErrors[index]?.communicationType;
        const providerError = contactDetailsErrors[index]?.provider;
        const prefixError = contactDetailsErrors[index]?.prefix;
        const detailsError =
          contactDetailsErrors[index]?.details || (index === 0 && !String(contact.details ?? "").trim());
        const locationError = contactDetailsErrors[index]?.location;
        const isProviderLocked = contact.communicationType === "Email";

        return (
          <div
            key={index}
            className="mb-4 flex w-max flex-row items-start gap-5"
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {/* Type */}
            <div className="flex flex-row">
              <div
                className={twMerge(
                  "flex h-[38px] w-[38px] flex-col items-center justify-center rounded-l-md border-y border-l",
                  communicationTypeError ? "border-y-red-600 border-l-red-600" : theme.uwhcAssistant.border,
                  theme.balticQuestionnaire.grayBg,
                )}
              >
                {contact.communicationType === "" && (
                  <QuestionMarkCircleIcon className={`h-5 w-5 ${communicationTypeError && "text-red-600"}`} />
                )}
                {contact.communicationType === "Satellite" && <PhoneIcon className="h-5 w-5" />}
                {contact.communicationType === "Mobile" && <DevicePhoneMobileIcon className="h-5 w-5" />}
                {contact.communicationType === "Fax" && <PrinterIcon className="h-5 w-5" />}
                {contact.communicationType === "Telex" && <PrinterIcon className="h-5 w-5" />}
                {contact.communicationType === "Email" && <EmailOperator className="h-5 w-5" />}
                {contact.communicationType === "Other" && <ListBulletIcon className="h-5 w-5" />}
              </div>
              <div className="flex flex-col">
                <select
                  name={`contactDetails.${index}.communicationType`}
                  value={contact.communicationType}
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={handleBlur}
                  onChange={(e) => handleInputChange(e, `contactDetails.${index}.communicationType` as Paths<T>)}
                  className={`h-[38px] w-[100px] rounded-r-md border p-2 text-[13px] focus:outline-none ${communicationTypeError && "border-red-600 text-red-600"} ${contact.communicationType === "" ? "text-gray-400" : theme.technicalQuestionnaire.lightGrayText} ${theme.uwhcAssistant.border}`}
                >
                  <option value="" disabled hidden>
                    Type
                  </option>
                  <option value="Satellite">Satellite</option>
                  <option value="Mobile">Mobile</option>
                  <option value="Fax">Fax</option>
                  <option value="Telex">Telex</option>
                  <option value="Email">Email</option>
                  <option value="Other">Other</option>
                </select>
                {communicationTypeError && (
                  <p className="mt-1 text-xs text-red-600">
                    {index === 0 ? "Select Email for the first contact" : "Field is empty"}
                  </p>
                )}

                {/* If 'Other' is selected, show additional input */}
                {contact.communicationType === "Other" && (
                  <input
                    type="text"
                    onFocus={() => setFocusedIndex(index)}
                    onBlur={handleBlur}
                    value={contact.customCommunicationType || ""}
                    onChange={(e) =>
                      handleInputChange(e, `contactDetails.${index}.customCommunicationType` as Paths<T>)
                    }
                    placeholder="Type"
                    className={twMerge(
                      "mt-2 h-[38px] w-[100px] rounded-md border p-3 text-[13px] text-gray-500 focus:outline-none",
                      theme.uwhcAssistant.border,
                    )}
                  />
                )}
              </div>
            </div>

            {/* Provider */}
            <div className="flex flex-col">
              <select
                value={contact.provider}
                disabled={isProviderLocked}
                data-test={`tech-form-contact-provider-${index}`}
                onFocus={() => setFocusedIndex(index)}
                onBlur={handleBlur}
                onChange={(e) => handleInputChange(e, `contactDetails.${index}.provider` as Paths<T>)}
                className={`h-[38px] w-[150px] rounded-md border p-2 text-[13px] focus:outline-none ${providerError && "border-red-600 text-red-600"} ${contact.provider === "" ? "text-gray-400" : theme.technicalQuestionnaire.lightGrayText} ${
                  isProviderLocked ? "cursor-not-allowed text-gray-400" : ""
                } ${theme.uwhcAssistant.border}`}
              >
                <option className="text-gray-400" value="" disabled hidden>
                  Provider
                </option>

                {isProviderLocked && <option value="N/A">N/A</option>}

                {contact.communicationType === "Mobile" && <option value="Other">Other</option>}

                {contact.communicationType === "Fax" && (
                  <>
                    <option value="Inmarsat">Inmarsat</option>
                    <option value="Other">Other</option>
                  </>
                )}

                {contact.communicationType === "Telex" && (
                  <>
                    <option value="Inmarsat">Inmarsat</option>
                    <option value="Other">Other</option>
                  </>
                )}

                {["Satellite", "Other", ""].includes(contact.communicationType) && (
                  <>
                    <option value="Inmarsat">Inmarsat</option>
                    <option value="VSAT">VSAT</option>
                    <option value="Starlink">Starlink</option>
                    <option value="Iridium">Iridium</option>
                    <option value="Intelsat">Intelsat</option>
                    <option value="Marlink">Marlink</option>
                    <option value="Thuraya">Thuraya</option>
                    <option value="Globalstar">Globalstar</option>
                    <option value="Other">Other</option>
                  </>
                )}
              </select>
              {providerError && <p className="mt-1 text-xs text-red-600">Field is empty</p>}

              {/* If 'Other' is selected, show additional input */}
              {contact.provider === "Other" && contact.communicationType !== "Email" && (
                <input
                  type="text"
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={handleBlur}
                  value={contact.customProvider || ""}
                  onChange={(e) => handleInputChange(e, `contactDetails.${index}.customProvider` as Paths<T>)}
                  placeholder="Provider"
                  className={twMerge(
                    "mt-2 h-[38px] w-[150px] rounded-md border p-3 text-[13px] text-gray-500 focus:outline-none",
                    theme.uwhcAssistant.border,
                  )}
                />
              )}
            </div>

            {/* Prefix */}
            {contact.communicationType !== "Email" && (
              <div className="flex flex-col">
                <select
                  value={contact.prefix}
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={handleBlur}
                  onChange={(e) => handleInputChange(e, `contactDetails.${index}.prefix` as Paths<T>)}
                  className={`h-[38px] w-[220px] rounded-md border p-2 text-[13px] focus:outline-none ${prefixError && "border-red-600 text-red-600"} ${contact.prefix === "" ? "text-gray-400" : theme.technicalQuestionnaire.lightGrayText} ${
                    contact.communicationType === "Email" ? "cursor-not-allowed text-gray-400" : ""
                  } ${theme.uwhcAssistant.border}`}
                >
                  <option value="" disabled hidden>
                    Select prefix
                  </option>
                  {contact.communicationType === "Mobile" && <CountryCodes />}
                  {contact.communicationType === "Fax" && <InmarsatSatelliteCodes />}

                  {contact.communicationType === "Telex" && contact.provider === "Inmarsat" && <InmarsatTelexCodes />}
                  {contact.communicationType === "Telex" && contact.provider !== "Inmarsat" && <TelexCodes />}

                  {contact.communicationType === "Satellite" && contact.provider === "Inmarsat" && (
                    <InmarsatSatelliteCodes />
                  )}
                  {contact.communicationType === "Satellite" && contact.provider === "Iridium" && (
                    <IridiumSatelliteCodes />
                  )}
                  {contact.communicationType === "Satellite" && contact.provider === "Thuraya" && (
                    <ThurayaSatelliteCodes />
                  )}
                  {contact.communicationType === "Satellite" && contact.provider === "Globalstar" && (
                    <GlobalStarSatelliteCodes />
                  )}

                  {((contact.communicationType === "Satellite" &&
                    !["Inmarsat", "Iridium", "Thuraya", "Globalstar"].includes(contact.provider)) ||
                    ["", "Other"].includes(contact.communicationType)) && (
                    <>
                      <option value="+">Prefix (+)</option>
                      <option value="00">Prefix (00)</option>
                    </>
                  )}

                  <option value="Other">Other</option>
                </select>
                {prefixError && <p className="mt-1 text-xs text-red-600">Field is empty</p>}

                {/* If 'Other' is selected, show additional input */}
                {contact.prefix === "Other" && (
                  <input
                    type="text"
                    onFocus={() => setFocusedIndex(index)}
                    onBlur={handleBlur}
                    value={contact.customPrefix || ""}
                    onChange={(e) => handleInputChange(e, `contactDetails.${index}.customPrefix` as Paths<T>)}
                    placeholder="Insert prefix"
                    className={twMerge(
                      "mt-2 h-[38px] w-[220px] rounded-md border p-3 text-[13px] text-gray-500 focus:outline-none",
                      theme.uwhcAssistant.border,
                    )}
                  />
                )}
              </div>
            )}

            {/* Details */}
            <div className="flex flex-col">
              <input
                type="text"
                name={`contactDetails.${index}.details`}
                aria-required={index === 0}
                aria-label={index === 0 ? "Vessel email address (required)" : "Contact address"}
                placeholder={index === 0 ? "Email address (required)" : "Insert contact"}
                onFocus={() => setFocusedIndex(index)}
                onBlur={handleBlur}
                value={contact.details}
                onChange={(e) => handleInputChange(e, `contactDetails.${index}.details` as Paths<T>)}
                className={`h-[38px] ${contact.communicationType === "Email" ? "w-[490px]" : "w-[250px]"} rounded-md border p-3 text-[13px] focus:outline-none ${detailsError && "border-red-600 placeholder-red-600"} ${theme.uwhcAssistant.border}`}
              />
              {detailsError && <p className="mt-1 text-xs text-red-600">Field is empty</p>}
            </div>

            {/* Location */}
            <div className="flex flex-col">
              <select
                value={contact.location}
                onFocus={() => setFocusedIndex(index)}
                disabled={contact.communicationType === "Email"}
                onBlur={handleBlur}
                onChange={(e) => handleInputChange(e, `contactDetails.${index}.location` as Paths<T>)}
                className={`h-[38px] w-[150px] rounded-md border p-2 text-[13px] focus:outline-none ${locationError && "border-red-600 text-red-600"} ${contact.location === "" ? "text-gray-400" : theme.technicalQuestionnaire.lightGrayText} ${
                  contact.communicationType === "Email" ? "cursor-not-allowed text-gray-400" : ""
                } ${theme.uwhcAssistant.border}`}
              >
                {contact.communicationType === "Email" ? (
                  <option value="N/A">N/A</option>
                ) : (
                  <>
                    <option value="" disabled hidden>
                      Select an option
                    </option>
                    <option value="Bridge">Bridge</option>
                    <option value="Master's cabin">Master's cabin</option>
                    <option value="Ship's Office">Ship's Office</option>
                    <option value="Other">Other</option>
                  </>
                )}
              </select>
              {locationError && <p className="mt-1 text-xs text-red-600">Field is empty</p>}

              {/* If 'Other' is selected, show additional input */}
              {contact.location === "Other" && contact.communicationType !== "Email" && (
                <input
                  type="text"
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={handleBlur}
                  value={contact.customLocation || ""}
                  onChange={(e) => handleInputChange(e, `contactDetails.${index}.customLocation` as Paths<T>)}
                  placeholder="Location"
                  className={twMerge(
                    "mt-2 h-[38px] w-[150px] rounded-md border p-3 text-[13px] text-gray-500 focus:outline-none",
                    theme.uwhcAssistant.border,
                  )}
                />
              )}
            </div>

            {/* Preferred */}
            <input
              type="checkbox"
              onFocus={() => setFocusedIndex(index)}
              onBlur={handleBlur}
              checked={contact.preferred}
              onChange={(e) => handleInputChange(e, `contactDetails.${index}.preferred` as Paths<T>)}
              className={twMerge("ml-[30px] mt-2.5 h-4 w-4 text-[14px] accent-green", theme.uwhcAssistant.border)}
            />

            {/* Edit Lines */}
            <div
              className={`ml-[70px] flex w-20 flex-row items-center justify-center gap-3 text-[12px] transition-opacity duration-200 ${
                focusedIndex === index || hoveredIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <button
                type="button"
                onClick={() => removeContactDetail(index)}
                disabled={form.contactDetails.length === 1}
                className={`mt-2 ${form.contactDetails.length === 1 && "cursor-not-allowed"}`}
              >
                <TrashIcon color="#cc0606" />
              </button>
            </div>
          </div>
        );
      })}
      <div className="mt-2">
        <button type="button" onClick={() => addContactDetail()} className="flex flex-row items-center gap-2">
          <PlusIcon className="text-red h-4 w-4 rounded-full border border-[#00a194]" color="#00a194" />
          <span className="text-[14px] leading-[14px] text-[#00a194]">Add another contact</span>
        </button>
      </div>
    </div>
  );
}

export default VesselContactDetails;
